/**
 * InvoiceCards - the S3 (failed) and S4 (pending) worklists below the table
 * breakpoint (SIH-17 I-8a, UX-T16 pages 05 and 06 at 390): one card per invoice,
 * one list item per card, every field labelled by its own term.
 *
 * The field set is exactly the table's, so nothing is added or hidden on a
 * phone. A value the source does not hold - null or the empty string - reads
 * "Not recorded by the source" (BA-approved copy); a value it holds is shown
 * verbatim and never reshaped (BR-1). An unfamiliar error code keeps its
 * "unmapped" marker, as in the table. A pending card's Status carries the same
 * "unmapped" qualifier as the pending table (N12). Long values wrap inside the card.
 */

import type { ReactNode } from 'react'
import { formatTimestamp } from '@domain/dates'
import type { InvoiceRow } from '@domain/types'
import { NOT_RECORDED_TEXT, heldValue } from './absentValue'
import { InvoiceIdLink } from './InvoiceIdLink'
import { ErrorCode } from './InvoiceTable'
import { PendingStatus } from './PendingStatus'


function Field({ label, testid, children }: { label: string; testid: string; children: ReactNode }) {
  return (
    <div className="invoice-card-field">
      <dt>{label}</dt>
      <dd data-testid={testid}>{children}</dd>
    </div>
  )
}

export function InvoiceCards({ rows, dbName }: { rows: readonly InvoiceRow[]; dbName: string }) {
  return (
    <ul className="invoice-cards" aria-label="Failed invoices" data-testid="invoice-cards">
      {rows.map((row) => (
        <li key={row.rectNo} className="invoice-card" data-testid={`invoice-card-${row.rectNo}`}>
          <p className="invoice-card-id">
            <InvoiceIdLink dbName={dbName} rectNo={row.rectNo} testid={`invoice-card-link-${row.rectNo}`}>
              {`Invoice ${row.rectNo}`}
            </InvoiceIdLink>
          </p>
          <dl className="invoice-card-fields">
            <Field label="Customer" testid={`invoice-card-customer-${row.rectNo}`}>
              {dbName}
            </Field>
            <Field label="TPIN / branch" testid={`invoice-card-branch-${row.rectNo}`}>
              {`${row.tin} / ${row.bhfId}`}
            </Field>
            <Field label="Submitted" testid={`invoice-card-submitted-${row.rectNo}`}>
              {heldValue(row.submissionTimestamp) ?? NOT_RECORDED_TEXT}
            </Field>
            <Field label="Error code" testid={`invoice-card-code-${row.rectNo}`}>
              <ErrorCode code={row.resultCd} />
            </Field>
            <Field label="Error message" testid={`invoice-card-message-${row.rectNo}`}>
              {heldValue(row.lastError) ?? NOT_RECORDED_TEXT}
            </Field>
          </dl>
        </li>
      ))}
    </ul>
  )
}

export function Submitted({ raw, now }: { raw: string | null; now: Date }) {
  const held = heldValue(raw)
  if (held === null) return <>{NOT_RECORDED_TEXT}</>
  const stamp = formatTimestamp(held, now)
  return (
    <span className="when">
      <span>{stamp.absolute}</span>
      {stamp.relative !== null && <span className="ago">{stamp.relative}</span>}
    </span>
  )
}

export function PendingInvoiceCards({ rows, dbName, now }: { rows: readonly InvoiceRow[]; dbName: string; now: Date }) {
  return (
    <ul className="invoice-cards" aria-label="Pending invoices" data-testid="pending-invoice-cards">
      {rows.map((row) => (
        <li key={row.rectNo} className="invoice-card" data-testid={`pending-card-${row.rectNo}`}>
          <p className="invoice-card-id">
            <InvoiceIdLink dbName={dbName} rectNo={row.rectNo} testid={`pending-card-link-${row.rectNo}`}>
              {`Invoice ${row.rectNo}`}
            </InvoiceIdLink>
          </p>
          <dl className="invoice-card-fields">
            <Field label="Customer" testid={`pending-card-customer-${row.rectNo}`}>
              {dbName}
            </Field>
            <Field label="TPIN / branch" testid={`pending-card-branch-${row.rectNo}`}>
              {`${row.tin} / ${row.bhfId}`}
            </Field>
            <Field label="Submitted" testid={`pending-card-submitted-${row.rectNo}`}>
              <Submitted raw={row.submissionTimestamp} now={now} />
            </Field>
            <Field label="Status" testid={`pending-card-status-${row.rectNo}`}>
              <PendingStatus rectNo={row.rectNo} resultCd={row.resultCd} />
            </Field>
          </dl>
        </li>
      ))}
    </ul>
  )
}
